import { FC, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { GetProgramFeeResponse } from "../../../../../../api/services/common/models";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
} from "../../../../../../components";
import { ExpandCollapseButton } from "../../../../../../components/expandCollapseButton/ExpandCollapseButton";
import PermissionWrapper from "../../../../../../components/permissionWrapper/PermissionWrapper";
import { TaxDetails } from "../../../../../../components/taxDetails/TaxDetails";
import {
  CURRENCY_FORMAT_MAP,
  CurrencyFormat,
} from "../../../../../../components/textFieldCurrency/CurrencyUtils";
import { MODULE_PERMISSIONS } from "../../../../../../helpers/constants";
import { commonSiteCoreKeys } from "../../../../../../helpers/constants/siteCoreKeys";
import useAccordion from "../../../../../../hooks/useAccordion";
import { useForceUpdate } from "../../../../../../hooks/useForceUpdate";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import {
  billingConfigurationDetailsFormKeys,
  ProgramDetail,
  taxDetailsFormKeys,
} from "../../../../../createProgram/modelsAndSchemas/Models";
import { programSiteCoreKeys } from "../../../../../createProgram/siteCore/SiteCoreKeys";
import { ViewProgramFee } from "./components/viewProgramFee/ViewProgramFee";

const initialAccordionStatus = {
  feeDetails: true,
  taxDetails: false,
};

interface ViewProgramFeeTabProps {
  programFeeDetailsData: GetProgramFeeResponse;
  programData?: ProgramDetail;
  pageId: string;
}

export const ViewProgramFeeTab: FC<ViewProgramFeeTabProps> = ({
  programFeeDetailsData,
  programData,
  pageId,
}) => {
  const { fetchComponentTitle, fetchLabel } = useSiteCore();

  const methods = useForm({
    mode: "all",
  });

  const { setValue } = methods;

  const {
    accordionStatus,
    handleIndividualAccordionChange,
    handleExpandCollapseAllChange,
  } = useAccordion(initialAccordionStatus);

  const forceUpdate = useForceUpdate();

  const renderHeader = (labelKey: string) => {
    return <AccordionHeaderText label={labelKey} />;
  };

  const getCurrencyName = () => {
    return programFeeDetailsData?.currency?.name ?? "";
  };

  const getCurrencyFormat = () => {
    const currencyFormat: CurrencyFormat =
      CURRENCY_FORMAT_MAP[getCurrencyName()];
    return currencyFormat ?? "";
  };

  useEffect(() => {
    if (!programFeeDetailsData) return;
    setValue(taxDetailsFormKeys.programFeeKey, programFeeDetailsData?.fee);
    setValue(taxDetailsFormKeys.taxEntriesKey, programFeeDetailsData?.taxes);
    setValue(
      billingConfigurationDetailsFormKeys.billingTypeIdKey,
      programFeeDetailsData?.billingTypeId,
    );
    setValue(
      billingConfigurationDetailsFormKeys.feePaymentTypeKey,
      programFeeDetailsData?.feePaymentType,
    );
    setValue(
      billingConfigurationDetailsFormKeys.billingSchedulesKey,
      programFeeDetailsData?.billingSchedules,
    );
    setValue(
      billingConfigurationDetailsFormKeys.schedulePeriodKey,
      programFeeDetailsData?.schedulePeriod,
    );
  }, [programFeeDetailsData]);

  return (
    <FormProvider {...methods}>
      <MuiBox>
        <ExpandCollapseButton
          accordionStatus={accordionStatus}
          handleExpandCollapseAllChange={handleExpandCollapseAllChange}
        />
        <PermissionWrapper permission={MODULE_PERMISSIONS.VIEW_PROGRAM_FEE}>
          <AccordionHeader
            wrapperClassName="fee-tax-details-accordion"
            title={renderHeader(
              fetchLabel(
                programSiteCoreKeys.programFeeTitle,
                programSiteCoreKeys.feeDetailsSectionHeading,
              ),
            )}
            onChange={() => handleIndividualAccordionChange("feeDetails")}
            isExpanded={accordionStatus.feeDetails}
          >
            <ViewProgramFee
              programFeeDetailsData={programFeeDetailsData}
              programData={programData}
              pageId={pageId}
            />
          </AccordionHeader>
        </PermissionWrapper>

        <PermissionWrapper permission={MODULE_PERMISSIONS.VIEW_TAX_DETAILS}>
          <AccordionHeader
            wrapperClassName="fee-tax-details-accordion"
            title={renderHeader(
              fetchComponentTitle(commonSiteCoreKeys.taxDetails),
            )}
            onChange={() => handleIndividualAccordionChange("taxDetails")}
            isExpanded={accordionStatus.taxDetails}
          >
            <TaxDetails
              pageId={pageId}
              currencyName={getCurrencyName()}
              currencyFormat={getCurrencyFormat()}
              currencyId={programFeeDetailsData?.currency?.id}
              forceUpdate={forceUpdate}
              isInEditMode={false}
              isInViewMode={true}
              taxDetailsFormKeys={taxDetailsFormKeys}
              programOrProjectFee={programFeeDetailsData?.fee?.toString() ?? ""}
            />
          </AccordionHeader>
        </PermissionWrapper>
      </MuiBox>
    </FormProvider>
  );
};
